/**
 * Các loại bản ghi trong bảng `records`. Mã loại là DỮ LIỆU (cột `records.kind`) nên giữ
 * nguyên.
 *
 * owned = true  → bản ghi có chủ sở hữu, lọc theo phạm vi nhìn.
 * owned = false → mục dùng chung; `write` nói ai được ghi: 'any' | 'config'.
 * office = true → bản ghi THUỘC MỘT VĂN PHÒNG (khoá `vp` trong `data`). Đây là bộ lọc CHỒNG THÊM
 *                 lên luật của chính loại đó: chỉ người của văn phòng ấy thấy và sửa (xem
 *                 `visibleOffices` ở API). Bản ghi cũ chưa có `vp` thì ai vào được mục đó đều thấy
 *                 — không làm dữ liệu đang dùng biến mất trong một lần nâng cấp.
 */
/** Khoá giữ MÃ VĂN PHÒNG trong `data` của các loại `office: true`. Là DỮ LIỆU — đừng đổi. */
export const OFFICE_KEY = 'vp';
/** Khoá giữ DANH SÁCH NGƯỜI ĐƯỢC TÍCH của một dự án sản xuất nhạc. Là DỮ LIỆU — đừng đổi. */
export const PROJECT_MEMBERS_KEY = 'mem';
export const RECORD_KINDS = {
    /** Kênh YouTube CỦA CHÍNH MÌNH (khác `competitor` là kênh đối thủ). */
    channel: { owned: true },
    video: { owned: true },
    playlist: { owned: true },
    prod: { owned: true },
    /**
     * Một dòng của bảng theo dõi KPI Producer (thêm 23/09/2026): ngày `d`, công việc `job` (mã trong
     * danh sách ở Cài đặt), công việc khác `oth` (tự gõ), dự án `pj` (id bản ghi `project`), tên bài
     * `song`, số sản phẩm `kpi`. CHỦ bản ghi là chính Producer — mỗi người thấy dòng của mình, quản lý
     * thấy cả đội, đúng phạm vi nhìn chung.
     *
     * KHÔNG nằm trong `PRODUCTION_KINDS`: Producer là Nhân viên, phải ghi được dòng của mình mà kho
     * bài hát vẫn đóng với họ (xem `canAccessProducerKpi`).
     */
    prodkpi: { owned: true },
    /** Một dòng CID. Thuộc văn phòng của THƯ MỤC chứa nó (`vp` chép sang để lọc thẳng trong SQL). */
    cid: { owned: true, office: true },
    /**
     * Mục «Lưu trữ» (27/09/2026): một dòng thông tin đăng nhập của MỘT KÊNH — mail `mail`, mật khẩu
     * `pw`, mã bí mật 2FA `otp`, mã dự phòng 2FA `otp2`, tên kênh `name`, link kênh `url`, network
     * `nw`, ghi chú `note`, mail khôi phục `kp`, nhóm `grp`, profile GPM nối `gpm` (11/10/2026).
     *
     * DÙNG CHUNG chứ không có chủ: ai mở được mục thì thấy và sửa mọi dòng — đây là kho của công ty,
     * không phải sổ riêng của người nhập. Ai mở được thì do Giám đốc TÍCH TÊN ở Cài đặt (xem
     * `canAccessVault`), chứ không theo chức vụ: đây là quyền vào được kênh, phải chỉ đích danh.
     */
    vault: { owned: false, write: 'any' },
    worklog: { owned: true },
    sched: { owned: true },
    /**
     * Bản lưu công việc của hệ thống cũ. MÀN HÌNH «Lưu trữ công việc» đã bỏ (26/09/2026) — chủ dự án
     * không dùng tới; khai ở đây để 4 bản ghi cũ vẫn đọc được nếu sau này cần lấy lại, chứ không còn
     * chỗ nào trong app hiện ra.
     */
    archive: { owned: true },
    competitor: { owned: true },
    /**
     * Dự án — gom việc được giao và tài nguyên (thumbnail, nhạc, video, link). DÙNG CHUNG chứ không «có
     * chủ»: người được giao việc phải thấy dự án dù người tạo ở ngoài phạm vi nhìn của họ; giao diện tự
     * giới hạn theo người tạo / người được giao / phạm vi (xem `domain/records/project.ts`).
     */
    project: { owned: false, write: 'any' },
    /** Một ảnh (thumbnail) của dự án — mỗi ảnh một bản ghi để dùng khe ảnh `thumb`. */
    projimg: { owned: false, write: 'any' },
    /**
     * FOLDER trong một dự án (`pj`, `name`) — gom ảnh và file gốc của thumbnail thành từng nhóm
     * («Bản nháp», «Đã duyệt», theo từng kênh…). Dùng chung như chính dự án: ai thấy dự án thì thấy
     * folder của nó.
     */
    projfd: { owned: false, write: 'any' },
    /** Sản lượng theo TUẦN của một dự án (video, thumbnail) — dùng chung như `project`, nối bằng `pj`. */
    projwk: { owned: false, write: 'any' },
    /** KPI: cấp bậc + hệ số của một người (`u`) — dùng chung, quản lý đặt. */
    kpistaff: { owned: false, write: 'any' },
    /** KPI tuần của một người (`u`) trên một dự án (`pj`) tuần `w` — dùng chung. */
    kpiweek: { owned: false, write: 'any' },
    /**
     * Đánh giá nhân sự tuần `w` của người `u` (giao ban thứ Hai): ba điểm 0–10 (`cm` chuyên môn, `qd` quy
     * định công ty, `yt` ý thức làm việc), nhận xét, ai chấm lúc nào — dùng chung, quản lý chấm.
     */
    kpieval: { owned: false, write: 'any' },
    /**
     * Báo cáo công việc HẰNG NGÀY của một người (`d` ngày): việc đã làm, số video / thumbnail, giờ làm, dự án,
     * vướng mắc, kế hoạch ngày mai. Có chủ; chính chủ xoá được.
     */
    report: { owned: true },
    /**
     * BẢNG TIN (03/10/2026): tin (`news`), ảnh của tin (`newsimg` — mỗi ảnh một bản ghi để dùng khe ảnh
     * `thumb`, như `projimg`; nối bằng `post`, xếp theo `i`), bình luận (`newscmt`), lượt thích (`newsrx`) —
     * dùng chung, ai cũng đọc. Luật ghi riêng ở `permissions.ts`: đăng tin và ảnh của tin chỉ Giám đốc và
     * người được tích «Người đăng tin»; bình luận và lượt thích thì ai cũng ghi nhưng chỉ sửa / bỏ của mình
     * (khoá `by` — cùng cách với nhận xét thumbnail). Xoá tin thì ảnh, bình luận và lượt thích của tin đi
     * theo (`records.service.ts`).
     */
    news: { owned: false, write: 'any' },
    newsimg: { owned: false, write: 'any' },
    newscmt: { owned: false, write: 'any' },
    newsrx: { owned: false, write: 'any' },
    /**
     * DỰ ÁN sản xuất nhạc. Khoá `mem` là DANH SÁCH NGƯỜI ĐƯỢC TÍCH (tên đăng nhập): Giám đốc lập dự
     * án rồi tích người liên quan, chỉ họ mới xem và review dự án ấy (chốt 23/09/2026). Dự án CHƯA
     * tích ai thì giữ nguyên nếp cũ — ai vào được mục Sản xuất nhạc đều thấy.
     */
    folder: { owned: false, write: 'any' },
    /**
     * Thư mục CID — một đợt gửi, THUỘC MỘT VĂN PHÒNG (`vp`, chốt 23/09/2026): người tạo phải nói rõ
     * thư mục này của văn phòng nào, và chỉ văn phòng ấy (cùng Giám đốc và bộ phận đầu mối CID) thấy.
     */
    cidfd: { owned: false, write: 'any', office: true, officeRequired: true },
    /**
     * BÁO CÁO CUỘC HỌP TUẦN của MỘT VĂN PHÒNG (thêm 23/09/2026): mỗi văn phòng mỗi tuần một bản —
     * chủ trì, người dự, nội dung chính, tồn đọng, việc giao và kế hoạch tuần tới.
     *
     * Dùng chung trong văn phòng (`write: 'any'`): cả phòng cùng đọc và cùng ghi thêm được, đúng như
     * biên bản họp — nhưng `officeRequired` nên phòng khác không thấy biên bản của nhau.
     */
    meeting: { owned: false, write: 'any', office: true, officeRequired: true },
    setting: { owned: false, write: 'config' },
};
export const RECORD_KIND_NAMES = Object.keys(RECORD_KINDS);
export function isRecordKind(value) {
    return typeof value === 'string' && Object.hasOwn(RECORD_KINDS, value);
}
/** Mục «Sản xuất nhạc» — chỉ Giám đốc và Trưởng phòng; máy chủ không gửi cho người khác. */
export const PRODUCTION_KINDS = new Set(['prod', 'folder']);
/** Mục «Quản lý CID» — khu vực riêng của bộ phận đầu mối CID và Giám đốc. */
export const CID_KINDS = new Set(['cid', 'cidfd']);
/**
 * Mục «Lưu trữ» — mail, mật khẩu và 2FA của từng kênh. Chỉ người được TÍCH TÊN; máy chủ không gửi
 * dòng nào cho người khác, kể cả Trưởng phòng.
 */
export const VAULT_KINDS = new Set(['vault']);
/**
 * Các khoá BÍ MẬT của một dòng Lưu trữ. Gom lại một chỗ để chỗ nào cần che hay cần đếm còn có một
 * đầu mối — ví dụ nhật ký chỉ ghi «đã xem mật khẩu của kênh X», không ghi chính mật khẩu.
 */
export const VAULT_SECRET_KEYS = ['pw', 'otp', 'otp2'];
/**
 * Khai báo của một loại, ở dạng ĐÃ MỞ RỘNG. `RECORD_KINDS` là `as const` nên kiểu suy ra của mỗi mục
 * chỉ có đúng những khoá mục đó khai — hỏi `.office` trên một mục không khai `office` là lỗi biên
 * dịch. Mọi chỗ đọc cờ của một loại đi qua đây.
 */
export function kindDefinition(kind) {
    return RECORD_KINDS[kind];
}
/** Loại mang văn phòng — lọc thêm theo khoá `vp` ngoài luật của chính loại đó. */
export const OFFICE_SCOPED_KINDS = new Set(RECORD_KIND_NAMES.filter((kind) => kindDefinition(kind).office === true));
/**
 * KPI và đánh giá nhân sự: dùng chung để ai cũng ĐỌC được dòng của mình, nhưng chỉ người nhìn thấy người
 * khác (Trưởng nhóm trở lên) mới GHI; và không ai tự đánh giá chính mình (`permissions.ts`).
 */
export const KPI_KINDS = new Set(['kpistaff', 'kpiweek', 'kpieval']);
/**
 * Loại bản ghi mà CHÍNH CHỦ luôn xoá được dù chức vụ không có quyền xoá chung: đều là thứ
 * từng người tự gõ tay cho riêng mình. Mở thêm loại nào phải cân nhắc kỹ.
 */
/**
 * KHOÁ MẬT của bản ghi kênh (thêm 26/09/2026): điều khoản hợp đồng với đối tác — bản hợp đồng, bên
 * trung gian, tỷ lệ ăn chia, hình thức thanh toán.
 *
 * Chỉ Giám đốc và cấp phòng (Trưởng phòng · Phó phòng) đọc và ghi. Máy chủ CẮT HẲN các khoá này
 * khỏi bản ghi trước khi gửi cho người khác — giấu ở giao diện thì ai mở công cụ nhà phát triển
 * cũng đọc được. Đổi lại, khi người không được xem lưu một kênh, máy chủ phải GIỮ NGUYÊN các khoá
 * ấy: trình duyệt của họ không có chúng để gửi lên, ghi đè nguyên `data` là điều khoản bay sạch.
 */
export const CHANNEL_SECRET_KEYS = ['hd', 'tg', 'tl', 'pay', 'dt'];
/**
 * KHOÁ RIÊNG CỦA CHỦ KÊNH (thêm 29/09/2026, chủ dự án: «mật khẩu, 2FA (ẩn ký tự) chỉ có chủ tài khoản
 * nhập mới xem được»): mật khẩu (`pw`) và mã bí mật 2FA (`otp`) của tài khoản giữ kênh — cùng tên khoá
 * với mục Lưu trữ.
 *
 * CHỈ NGƯỜI PHỤ TRÁCH (chủ bản ghi) đọc và ghi — kể cả Giám đốc và cấp phòng cũng không nhận về máy.
 * Máy chủ cắt hẳn khi gửi cho người khác, và khi người khác lưu kênh thì giữ nguyên phần đang lưu và
 * không cho đặt giá trị mới — cùng cách với `CHANNEL_SECRET_KEYS`. Bàn giao kênh thì chúng đi theo kênh
 * sang người phụ trách mới (giao kênh là giao cả tài khoản).
 */
export const CHANNEL_OWNER_SECRET_KEYS = ['pw', 'otp'];
export const SELF_DELETABLE_KINDS = new Set([
    'competitor',
    'archive',
    'channel',
    'report',
    /**
     * Video (thêm 23/09/2026): gõ nhầm NGÀY hay nhầm KÊNH thì chính người nhập phải gỡ được, không
     * phải nhờ Trưởng phòng. Cân nhắc kỹ vì báo cáo tháng đọc từ đây — bù lại mỗi lần xoá đều vào
     * nhật ký (`record.deleted`), và người khác vẫn không đụng được video của mình.
     */
    'video',
    /**
     * Dòng tiến độ hằng ngày của Producer (thêm 26/09/2026): cùng hình dạng với `report` — một dòng
     * nhật ký của chính mình, ghi nhầm thì phải tự gỡ được.
     *
     * Trước đó nút xoá vẫn hiện trên dòng của chính họ nhưng bấm vào luôn nhận «Chỉ Giám đốc /
     * Trưởng phòng mới được xoá» — một cái nút không bao giờ làm được việc của nó.
     */
    'prodkpi',
]);
//# sourceMappingURL=record-kinds.js.map